import { PlayCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Modal } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { getVideoMetadata } from 'utils/validator/video';
import { openExternalURL } from '../../utils/openExternalURL';
import ProxyVideo from 'components/ProxyVideo';

const Container = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid var(--fett-border-default);
  border-radius: 2px;
  .thumb {
    background: rgba(237, 245, 255, 1);
    border-radius: 2px;
    position: relative;
    width: 100%;
    height: 100%;
    cursor: pointer;
    .duration {
      position: absolute;
      bottom: 3px;
      right: 3px;
      color: #ffffff;
      font-size: 12px;
      background: rgba(35, 36, 38, 1);
      border-radius: 12px;
      opacity: 0.47;
      padding: 1px 3px;
    }
    .play {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: #ffffff;
      z-index: 10;
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
  .video-name {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: var(--fett-text-secondary);
    height: 18px;
    line-height: 18px;
    margin: 6px 0;
  }
  .video-action {
    button {
      background: rgba(215, 225, 250, 1);
      border-radius: 2px;
      color: var(--fett-scenario-accent-text);
      width: 100%;
    }
  }
`;
const modalCss = css`
  top: 0;
  width: 100vw !important;
  max-width: 100vw;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  padding: 0;
  margin: 0;
  .ant-modal-content {
    width: 100%;
    overflow: hidden;
    height: 100%;
    .ant-modal-body {
      width: 100%;
      height: 100%;
      height: calc(100vh - 55px);
      padding: 0;
      overflow: auto;
      background-color: #eef0f4;
    }
  }
`;
const ModalContent = styled.div`
  width: 100%;
  margin: 0;
  height: 100%;
  padding: 20px;
  video {
    margin: 0;
    width: 100%;
    height: 100%;
  }

  .right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-left: 7px;
    color: var(--fett-text-tertiary);
    a {
      padding: 0;
      text-align: left;
    }
  }
`;
interface Props {
  url?: string;
  thumbUrl?: string;
  name?: string;
  durationSeconds?: number;
  className?: string;
  mask?: boolean;
  openExternal?: boolean;
}
const duration = (durationSeconds: number) => {
  if (!durationSeconds) {
    return '';
  }
  const minute = Math.floor(durationSeconds / 60);
  const second = durationSeconds % 60;
  return `${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
};
const PreviewVideo = (props: Props) => {
  const {
    url,
    thumbUrl,
    name,
    durationSeconds,
    className,
    mask = false,
    openExternal = false,
  } = props;
  const [play, setPlay] = useState(false);
  const [durationLocal, setDurationLocal] = useState(0);
  const displayTime = useMemo(() => {
    if (durationSeconds) {
      return duration(durationSeconds);
    }
    if (durationLocal) {
      return duration(durationLocal);
    }
    return undefined;
  }, [durationLocal, durationSeconds]);
  useEffect(() => {
    if (!durationSeconds && url) {
      getVideoMetadata(url).then((res) => {
        setDurationLocal(Number(res.duration.toFixed(0)));
      });
    }
  }, [durationSeconds, url]);
  return (
    <Container className={className}>
      <div
        className="thumb"
        onClick={() => {
          if (openExternal && url) {
            openExternalURL(url);
          } else {
            setPlay(true);
          }
        }}
      >
        {mask && <div className="bg-[#00000078] absolute left-0 right-0 top-0 bottom-0" />}
        <img src={thumbUrl} alt="" />
        <PlayCircleOutlined className="play" />
        {displayTime && <div className="duration">{displayTime}</div>}
      </div>
      <Modal
        width={700}
        visible={play}
        title={name}
        destroyOnClose
        footer={null}
        zIndex={99999}
        className={modalCss}
        onCancel={() => setPlay(false)}
      >
        <ModalContent>
          <ProxyVideo controls autoPlay src={url} controlsList="nodownload">
            <track kind="captions" />
          </ProxyVideo>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default PreviewVideo;
